/* ==========================================================================
   EVIGIEE — global behaviour
   Header, overlays, cart drawer, AJAX cart, quantity steppers, accordions,
   toasts. No framework, no dependencies.
   ========================================================================== */

(function () {
  'use strict';

  const routes = (window.theme && window.theme.routes) || {};
  const strings = (window.theme && window.theme.strings) || {};

  /* ---------- helpers ---------- */

  const $ = (sel, root) => (root || document).querySelector(sel);
  const $$ = (sel, root) => Array.from((root || document).querySelectorAll(sel));

  function focusables(root) {
    return $$(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      root
    ).filter((el) => el.offsetParent !== null);
  }

  window.themeUtils = {
    toast(message) {
      const el = $('[data-toast]');
      if (!el) return;
      $('[data-toast-text]', el).textContent = message;
      el.hidden = false;
      clearTimeout(el._timer);
      el._timer = setTimeout(() => {
        el.hidden = true;
      }, 2600);
    },

    formatMoney(cents) {
      const format = (window.theme && window.theme.moneyFormat) || '${{amount}}';
      const value = (cents / 100).toFixed(2);
      const parts = value.split('.');
      const withSeparators = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return format
        .replace(/\{\{\s*amount\s*\}\}/, withSeparators + '.' + parts[1])
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/, withSeparators)
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, withSeparators.replace(/,/g, '.') + ',' + parts[1])
        .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, withSeparators.replace(/,/g, '.'))
        .replace(/\{\{\s*amount_with_space_separator\s*\}\}/, withSeparators.replace(/,/g, ' ') + ',' + parts[1])
        .replace(/\{\{\s*amount_no_decimals_with_space_separator\s*\}\}/, withSeparators.replace(/,/g, ' '));
    },

    async fetchSections(sectionIds) {
      const url = `${routes.root || '/'}?sections=${sectionIds.join(',')}`;
      const res = await fetch(url.replace('//?', '/?'));
      if (!res.ok) throw new Error('Section render failed');
      return res.json();
    }
  };

  /* ---------- overlay manager ---------- */

  const Overlay = {
    current: null,

    open(el, opener) {
      if (!el) return;
      this.close();
      this.current = el;
      this.opener = opener || document.activeElement;
      el.hidden = false;
      const scrim = $('[data-scrim]');
      if (scrim) scrim.hidden = false;
      document.body.classList.add('overlay-open');
      const first = focusables(el)[0];
      if (first) first.focus();
      el.setAttribute('aria-hidden', 'false');
    },

    close() {
      if (!this.current) return;
      this.current.hidden = true;
      this.current.setAttribute('aria-hidden', 'true');
      this.current = null;
      const scrim = $('[data-scrim]');
      if (scrim) scrim.hidden = true;
      document.body.classList.remove('overlay-open');
      if (this.opener && document.contains(this.opener)) this.opener.focus();
      this.opener = null;
    },

    trap(event) {
      if (!this.current || event.key !== 'Tab') return;
      const items = focusables(this.current);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  };

  window.themeOverlay = Overlay;

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      const lightbox = $('[data-lightbox]');
      if (lightbox && !lightbox.hidden) return; // gallery handles its own
      Overlay.close();
    }
    Overlay.trap(e);
  });

  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-scrim]') || e.target.closest('[data-close-overlay]')) {
      Overlay.close();
    }
  });

  /* ---------- header ---------- */

  function initHeader() {
    const header = $('[data-header]');
    if (!header) return;

    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        header.classList.toggle('header--scrolled', window.scrollY > 40);
        ticking = false;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    // Mega menu — hover on desktop, click/keyboard everywhere.
    // Closing is handled at the header level (not per item) so the pointer can
    // travel from a nav link down into the panel without the menu snapping shut.
    const hasHover = window.matchMedia('(hover: hover) and (min-width: 901px)').matches;

    const closeAllMenus = () => {
      $$('[data-megamenu]', header).forEach((p) => {
        p.hidden = true;
      });
      $$('[data-menu-trigger]', header).forEach((t) => t.setAttribute('aria-expanded', 'false'));
    };

    let closeTimer;
    const cancelClose = () => clearTimeout(closeTimer);
    const scheduleClose = () => {
      cancelClose();
      closeTimer = setTimeout(closeAllMenus, 180);
    };

    $$('[data-menu-item]', header).forEach((item) => {
      const panel = $('[data-megamenu]', item);
      const trigger = $('[data-menu-trigger]', item);
      if (!trigger) return;

      const openThis = () => {
        cancelClose();
        closeAllMenus();
        if (panel) {
          panel.hidden = false;
          trigger.setAttribute('aria-expanded', 'true');
        }
      };

      if (hasHover) {
        // Hovering any top-level item opens its panel (or closes others if it has none).
        item.addEventListener('mouseenter', openThis);
        // Keep the panel open while the pointer is inside it.
        if (panel) {
          panel.addEventListener('mouseenter', cancelClose);
        }
      }

      trigger.addEventListener('focus', openThis);
      trigger.addEventListener('click', (e) => {
        if (panel && panel.hidden) {
          e.preventDefault();
          openThis();
        } else if (panel) {
          closeAllMenus();
        }
      });
      item.addEventListener('focusout', (e) => {
        if (panel && !item.contains(e.relatedTarget)) closeAllMenus();
      });
    });

    // A short grace period on leave lets the mouse cross any small gap between
    // the nav row and the panel; re-entering the header cancels the close.
    header.addEventListener('mouseleave', scheduleClose);
    header.addEventListener('mouseenter', cancelClose);

    // Openers
    $$('[data-open]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        const target = $(`[data-overlay="${btn.dataset.open}"]`);
        if (!target) return;
        e.preventDefault();
        Overlay.open(target, btn);
        if (btn.dataset.open === 'search') {
          const input = $('input[type="search"], input[name="q"]', target);
          if (input) setTimeout(() => input.focus(), 60);
        }
      });
    });

    // Mobile nav accordions
    $$('[data-mobile-toggle]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const expanded = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', String(!expanded));
        const panel = document.getElementById(btn.getAttribute('aria-controls'));
        if (panel) panel.hidden = expanded;
      });
    });
  }

  /* ---------- quantity stepper ---------- */

  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-qty-input]');
      if (!this.input) return;
      this.minus = this.querySelector('[data-qty-minus]');
      this.plus = this.querySelector('[data-qty-plus]');

      this.minus.addEventListener('click', () => this.step(-1));
      this.plus.addEventListener('click', () => this.step(1));
      this.input.addEventListener('change', () => this.validate());
      this.validate();
    }

    step(direction) {
      const value = parseInt(this.input.value, 10) || 1;
      this.input.value = value + direction;
      this.validate();
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    validate() {
      const min = parseInt(this.input.min, 10) || 1;
      const max = this.input.max ? parseInt(this.input.max, 10) : null;
      let value = parseInt(this.input.value, 10);
      if (isNaN(value)) value = min;
      if (value < min) value = min;
      if (max !== null && value > max) value = max;
      this.input.value = value;
      if (this.minus) this.minus.disabled = value <= min;
      if (this.plus) this.plus.disabled = max !== null && value >= max;
    }
  }

  if (!customElements.get('quantity-input')) {
    customElements.define('quantity-input', QuantityInput);
  }

  /* ---------- accordions ---------- */

  function initAccordions(root) {
    $$('[data-accordion-toggle]', root || document).forEach((btn) => {
      if (btn._bound) return;
      btn._bound = true;
      btn.addEventListener('click', () => {
        const expanded = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', String(!expanded));
        const panel = document.getElementById(btn.getAttribute('aria-controls'));
        if (panel) panel.hidden = expanded;
      });
    });
  }

  window.themeInitAccordions = initAccordions;

  /* ---------- cart ---------- */

  const Cart = {
    async add(formDataOrPayload) {
      const isForm = formDataOrPayload instanceof FormData;
      const sections = this.sectionsToRender();

      let body;
      const headers = { Accept: 'application/json' };

      if (isForm) {
        formDataOrPayload.append('sections', sections.join(','));
        formDataOrPayload.append('sections_url', window.location.pathname);
        body = formDataOrPayload;
      } else {
        headers['Content-Type'] = 'application/json';
        body = JSON.stringify(
          Object.assign({}, formDataOrPayload, {
            sections: sections,
            sections_url: window.location.pathname
          })
        );
      }

      const res = await fetch(routes.cart_add || '/cart/add.js', { method: 'POST', headers, body });
      const data = await res.json();

      if (!res.ok || data.status) {
        const message = data.description || data.message || strings.error;
        const error = new Error(message);
        error.data = data;
        throw error;
      }

      this.renderSections(data.sections);
      this.refreshCount();
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
      return data;
    },

    async change(payload) {
      const res = await fetch(routes.cart_change || '/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(
          Object.assign({}, payload, {
            sections: this.sectionsToRender(),
            sections_url: window.location.pathname
          })
        )
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.description || strings.error);
      this.renderSections(data.sections);
      this.refreshCount(data.item_count);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
      return data;
    },

    sectionsToRender() {
      const ids = $$('[data-cart-section]').map((el) => el.dataset.sectionId).filter(Boolean);
      return ids.length ? ids : ['cart-drawer'];
    },

    renderSections(sections) {
      if (!sections) return;
      Object.keys(sections).forEach((id) => {
        const html = sections[id];
        if (!html) return;
        const target = $(`[data-section-id="${id}"]`);
        if (!target) return;
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const incoming = parsed.querySelector(`[data-section-id="${id}"]`);
        if (incoming) target.innerHTML = incoming.innerHTML;
      });
      initAccordions();
    },

    async refreshCount(count) {
      let itemCount = count;
      if (typeof itemCount !== 'number') {
        const res = await fetch(`${routes.cart || '/cart'}.js`, { headers: { Accept: 'application/json' } });
        const cart = await res.json();
        itemCount = cart.item_count;
      }
      $$('[data-cart-count]').forEach((el) => {
        el.textContent = itemCount;
        el.hidden = itemCount === 0;
      });
    },

    open() {
      const drawer = $('[data-overlay="cart"]');
      if (drawer) Overlay.open(drawer, $('[data-open="cart"]'));
    }
  };

  window.themeCart = Cart;

  /* ---------- cart drawer interactions (delegated, survives re-render) ---------- */

  document.addEventListener('click', async (e) => {
    const remove = e.target.closest('[data-cart-remove]');
    if (remove) {
      e.preventDefault();
      try {
        await Cart.change({ id: remove.dataset.cartRemove, quantity: 0 });
      } catch (err) {
        window.themeUtils.toast(err.message);
      }
      return;
    }

    const quickAdd = e.target.closest('[data-quick-add]');
    if (quickAdd && quickAdd.dataset.variantId) {
      e.preventDefault();
      quickAdd.setAttribute('aria-busy', 'true');
      try {
        await Cart.add({ items: [{ id: Number(quickAdd.dataset.variantId), quantity: 1 }] });
        if ((window.theme && window.theme.cartType) === 'drawer') Cart.open();
        window.themeUtils.toast(strings.added || 'Added to cart');
      } catch (err) {
        window.themeUtils.toast(err.message);
      } finally {
        quickAdd.removeAttribute('aria-busy');
      }
    }
  });

  document.addEventListener('change', async (e) => {
    const input = e.target.closest('[data-line-key]');
    if (!input) return;
    const quantity = parseInt(input.value, 10);
    try {
      await Cart.change({ id: input.dataset.lineKey, quantity: quantity });
    } catch (err) {
      window.themeUtils.toast(err.message);
    }
  });

  /* ---------- share ---------- */

  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-share]');
    if (!btn) return;
    const url = btn.dataset.shareUrl;
    if (navigator.share) {
      try {
        await navigator.share({ title: btn.dataset.shareTitle, url });
      } catch (err) {
        /* user cancelled */
      }
    } else if (navigator.clipboard) {
      await navigator.clipboard.writeText(url);
      window.themeUtils.toast('Link copied');
    }
  });

  /* ---------- recently viewed store ---------- */

  function recordRecentlyViewed() {
    const root = $('[data-product-root]');
    if (!root) return;
    const handle = document.body.dataset.productHandle;
    if (!handle) return;
    try {
      const key = 'evigiee:recently-viewed';
      const list = JSON.parse(localStorage.getItem(key) || '[]').filter((h) => h !== handle);
      list.unshift(handle);
      localStorage.setItem(key, JSON.stringify(list.slice(0, 12)));
    } catch (err) {
      /* private mode */
    }
  }

  /* ---------- boot ---------- */

  function init() {
    initHeader();
    initAccordions();
    recordRecentlyViewed();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor support
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:select', () => Overlay.close());
})();
